// data/progress-store.js — IndexedDB persistence for play records and settings.
//
// Mirrors `Assets/_App/Engines/ProgressManager.cs` from the Unity port, but
// uses IndexedDB instead of `Application.persistentDataPath/progress.json`
// (browsers don't have arbitrary file write access).
//
// Object stores:
//   playRecords:  keyPath = itemId,  value = { itemId, playCount, playheadSec, completed, lastPlayedUtc }
//   settings:     keyPath = key,     value = { key, value }    (used by language-store.js)
//
// All methods are async; the tiny wrapper below avoids the verbose IndexedDB
// callback dance and keeps us free of any external dep.

/** @typedef {import("../types").PlayRecord} PlayRecord */

/** @type {IDBDatabase | null} */ let _db = null;

const NAME = "rm-learner";
const VERSION = 1;

export async function openProgressStore() {
  if (_db) return _db;
  _db = await new Promise((resolve, reject) => {
    const req = indexedDB.open(NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("playRecords")) db.createObjectStore("playRecords", { keyPath: "itemId" });
      if (!db.objectStoreNames.contains("settings")) db.createObjectStore("settings", { keyPath: "key" });
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
  return _db;
}

/**
 * @param {string} store
 * @param {IDBTransactionMode} mode
 * @param {(s: IDBObjectStore) => IDBRequest | void} fn
 * @returns {Promise<any>}
 */
async function withStore(store, mode, fn) {
  const db = await openProgressStore();
  if (!db) throw new Error("[progress-store] not opened");
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const s = tx.objectStore(store);
    const req = fn(s);
    tx.oncomplete = () => resolve(req && "result" in req ? req.result : undefined);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

// ── Play records ─────────────────────────────────────────────────────────────

/** @param {string} itemId @returns {Promise<PlayRecord|undefined>} */
export function getPlayRecord(itemId) {
  return withStore("playRecords", "readonly", (s) => s.get(itemId));
}

/**
 * Mirrors ProgressManager.RecordPlay(): increment play count and record
 * lastPlayedUtc. Caller decides whether COMPLETION_THRESHOLD has been met
 * (engines/progress-manager.js handles that policy).
 *
 * @param {string} itemId
 * @returns {Promise<PlayRecord>}
 */
export async function recordPlay(itemId) {
  const existing = (await getPlayRecord(itemId)) ?? makeBlank(itemId);
  existing.playCount += 1;
  existing.lastPlayedUtc = Date.now();
  await withStore("playRecords", "readwrite", (s) => s.put(existing));
  return existing;
}

/**
 * Mirrors ProgressManager.SavePlayhead(). Persist the current playback
 * position so the user can resume on a later session.
 *
 * @param {string} itemId
 * @param {number} timeSec
 */
export async function savePlayhead(itemId, timeSec) {
  const existing = (await getPlayRecord(itemId)) ?? makeBlank(itemId);
  existing.playheadSec = timeSec;
  await withStore("playRecords", "readwrite", (s) => s.put(existing));
}

/**
 * @param {string} itemId
 * @param {boolean} completed
 */
export async function setCompleted(itemId, completed) {
  const existing = (await getPlayRecord(itemId)) ?? makeBlank(itemId);
  existing.completed = completed;
  await withStore("playRecords", "readwrite", (s) => s.put(existing));
}

/** @returns {Promise<PlayRecord[]>} */
export async function getAllPlayRecords() {
  const all = await withStore("playRecords", "readonly", (s) => s.getAll());
  return /** @type {PlayRecord[]} */ (all ?? []);
}

/** @returns {Promise<void>} */
export async function resetAll() {
  await withStore("playRecords", "readwrite", (s) => s.clear());
}

// ── Settings ─────────────────────────────────────────────────────────────────

/** @param {string} key @param {unknown} value */
export async function setSetting(key, value) {
  await withStore("settings", "readwrite", (s) => s.put({ key, value }));
}

/** @param {string} key @returns {Promise<unknown>} */
export async function getSetting(key) {
  const r = await withStore("settings", "readonly", (s) => s.get(key));
  return r?.value;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** @param {string} itemId @returns {PlayRecord} */
function makeBlank(itemId) {
  return { itemId, playCount: 0, playheadSec: 0, completed: false, lastPlayedUtc: 0 };
}
